import React, { useState } from 'react';
import { addDays, format } from 'date-fns';
import {
  CalendarIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  NoSymbolIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  AVAILABILITY_TYPES,
  TIME_SLOTS,
  DAYS_OF_WEEK,
  DAYS_OF_WEEK_SHORT,
  getWeekDates,
  formatWeekRange,
  getSlotStateLabel,
  getWeeklyStats,
  isAvailabilitySlotUnset,
} from './AvailabilityTypes';
import { useNotifications } from '../notifications';
import LoadingSpinner from '../LoadingSpinner';
import CalendarEventOverlay from '../calendar/CalendarEventOverlay';
import {
  processEventBlocks,
  calculateEventBlockDimensions,
} from '../../utils/calendarUtils';

const DesktopAvailabilityGrid = ({
  availability,
  setAvailability,
  selectedWeek,
  loading,
  isSaving,
  lastSaved,
  hasUnsavedChanges,
  triggerSave,
  navigateWeek,
  saveAvailability,
  copyToNextWeek,
  showNotification,
  dragMode,
  setDragMode,
  isDragging,
  setIsDragging,
  // New props for crewmates overlay
  showCrewmates,
  setShowCrewmates,
  crewOptions,
  crewFilterIds,
  setCrewFilterIds,
  crewmateCountsMap,
  totalCrewmates,
  loadingCrewmates,
  // Calendar events prop
  calendarEvents = [],
  calendarIntegrations = [],
  filteredCalendarEvents = [],
  loadingCalendarEvents = false,
  // Calendar modal props
  enabledCalendars = new Set(),
  toggleCalendar = () => { },
  // Calendar view toggle
  showCalendarView = true,
  setShowCalendarView = () => { },
}) => {
  // Use the notification system
  const { showConfirm, showAlert, showInfo } = useNotifications();
  const weekDates = getWeekDates(selectedWeek);
  const weeklyStats = getWeeklyStats(availability);
  const [compactTimeDisplay, setCompactTimeDisplay] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [dragStartCell, setDragStartCell] = useState(null);
  const [desktopGridHelpOpen, setDesktopGridHelpOpen] = useState(false);

  const handleCellClick = (dayIndex, timeSlot) => {
    // Don't process click if we're dragging
    if (isDragging) return;

    const dayData = availability[dayIndex];
    let newType;

    if (isAvailabilitySlotUnset(dayData, timeSlot)) {
      newType = AVAILABILITY_TYPES.AVAILABLE;
    } else {
      const currentType = dayData[timeSlot];
      switch (currentType) {
        case AVAILABILITY_TYPES.UNAVAILABLE:
          newType = AVAILABILITY_TYPES.AVAILABLE;
          break;
        case AVAILABILITY_TYPES.AVAILABLE:
          newType = AVAILABILITY_TYPES.PREFERRED;
          break;
        case AVAILABILITY_TYPES.PREFERRED:
          newType = AVAILABILITY_TYPES.AVAILABLE;
          break;
        default:
          newType = AVAILABILITY_TYPES.AVAILABLE;
      }
    }

    updateCell(dayIndex, timeSlot, newType);
  };

  const handleAddAvailability = (dayIndex, timeSlot) => {
    // Don't process click if we're dragging
    if (isDragging) return;

    // Always set to available, regardless of current state
    updateCell(dayIndex, timeSlot, AVAILABILITY_TYPES.AVAILABLE);
  };

  const handleRightClick = (e, dayIndex, timeSlot) => {
    e.preventDefault(); // Prevent context menu
    e.stopPropagation(); // Stop event bubbling
    updateCell(dayIndex, timeSlot, AVAILABILITY_TYPES.UNAVAILABLE);
  };

  // Mouse handlers for drag functionality
  const handleMouseDown = (e, dayIndex, timeSlot) => {
    e.preventDefault(); // Prevent text selection

    if (e.button === 0) {
      // Left mouse button - always create available slots when dragging
      setDragMode && setDragMode(AVAILABILITY_TYPES.AVAILABLE);
      setIsDragging && setIsDragging(true);
      updateCell(dayIndex, timeSlot, AVAILABILITY_TYPES.AVAILABLE);
    } else if (e.button === 2) {
      // Right mouse button - always make unavailable when dragging
      setDragMode && setDragMode(AVAILABILITY_TYPES.UNAVAILABLE);
      setIsDragging && setIsDragging(true);
      updateCell(dayIndex, timeSlot, AVAILABILITY_TYPES.UNAVAILABLE);
    }
  };

  const handleMouseEnter = (dayIndex, timeSlot) => {
    if (isDragging && dragMode) {
      updateCell(dayIndex, timeSlot, dragMode);
    }
  };

  const handleMouseUp = () => {
    setIsDragging && setIsDragging(false);
    setDragMode && setDragMode(null);
  };

  const updateCell = (dayIndex, timeSlot, type) => {
    setAvailability((prev) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        [timeSlot]: type,
      },
    }));
  };

  const getCellClass = (dayIndex, timeSlot) => {
    const dayData = availability[dayIndex];
    const baseClass =
      'relative w-full h-5 cursor-pointer border transition-colors duration-150 select-none';

    if (isAvailabilitySlotUnset(dayData, timeSlot)) {
      return `${baseClass} border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 active:bg-gray-100`;
    }

    const type = dayData[timeSlot];
    switch (type) {
      case AVAILABILITY_TYPES.AVAILABLE:
        return `${baseClass} border-gray-200 bg-blue-200 hover:bg-blue-300 active:bg-blue-400`;
      case AVAILABILITY_TYPES.PREFERRED:
        return `${baseClass} border-gray-200 bg-green-200 hover:bg-green-300 active:bg-green-400`;
      case AVAILABILITY_TYPES.UNAVAILABLE:
        return `${baseClass} border-red-300 bg-red-200 hover:bg-red-300 active:bg-red-400`;
      default:
        return `${baseClass} border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 active:bg-gray-100`;
    }
  };

  // Function to get crew-mates availability color based on percentage
  const getCrewmatesAvailabilityColor = (counts, totalCrewmates) => {
    if (!counts || !totalCrewmates) return null;

    const total = (counts.available || 0) + (counts.preferred || 0);
    if (total === 0) return null;

    const percentage = (total / totalCrewmates) * 100;

    // Color coding based on availability percentage
    if (percentage >= 80) {
      return 'bg-green-500/60'; // High availability - green
    } else if (percentage >= 60) {
      return 'bg-green-400/50'; // Good availability - light green
    } else if (percentage >= 40) {
      return 'bg-yellow-400/50'; // Moderate availability - yellow
    } else if (percentage >= 20) {
      return 'bg-orange-400/50'; // Low availability - orange
    } else {
      return 'bg-red-400/50'; // Very low availability - red
    }
  };

  // Function to get crew-mates availability text color
  const getCrewmatesTextColor = (counts, totalCrewmates) => {
    if (!counts || !totalCrewmates) return 'text-gray-600';

    const total = (counts.available || 0) + (counts.preferred || 0);
    if (total === 0) return 'text-gray-600';

    const percentage = (total / totalCrewmates) * 100;

    if (percentage >= 60) {
      return 'text-white font-semibold'; // High availability - white text
    } else {
      return 'text-gray-800 font-medium'; // Lower availability - dark text
    }
  };

  const applyPreset = (preset) => {
    // Start with current availability instead of empty object
    const newAvailability = JSON.parse(JSON.stringify(availability));

    // Initialize empty day objects if they don't exist
    for (let day = 0; day < 7; day++) {
      if (!newAvailability[day]) {
        newAvailability[day] = {};
      }
    }

    switch (preset) {
      case 'earlyBird':
        for (let day = 0; day < 7; day++) {
          // Set early morning slots (05:30-07:30) as preferred
          for (let i = 1; i < 6; i++) {
            newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.PREFERRED;
          }
          // Set morning slots (07:30-09:00) as available
          for (let i = 6; i < 9; i++) {
            newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.AVAILABLE;
          }
        }
        break;
      case 'evening':
        for (let day = 0; day < 7; day++) {
          // Set evening slots (17:00-19:30) as preferred
          for (let i = 24; i < 29; i++) {
            newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.PREFERRED;
          }
          // Set late evening (19:30-21:00) as available
          for (let i = 29; i < 32; i++) {
            if (TIME_SLOTS[i]) {
              newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.AVAILABLE;
            }
          }
        }
        break;
      case 'weekends':
        // Only weekends (Saturday=5, Sunday=6)
        for (const day of [5, 6]) {
          // Set morning to evening as preferred
          for (let i = 3; i < 20; i++) {
            newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.PREFERRED;
          }
        }
        break;
      case 'weekdays':
        // Weekdays only (Mon-Fri = 0-4)
        for (let day = 0; day < 5; day++) {
          // Morning slots
          for (let i = 2; i < 8; i++) {
            newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.AVAILABLE;
          }
          // Evening slots
          for (let i = 24; i < 30; i++) {
            newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.AVAILABLE;
          }
        }
        break;
      case 'flexible':
        for (let day = 0; day < 7; day++) {
          // Set all slots as available (very flexible)
          for (let i = 0; i < TIME_SLOTS.length; i++) {
            newAvailability[day][TIME_SLOTS[i]] = AVAILABILITY_TYPES.AVAILABLE;
          }
        }
        break;
      case 'clear':
        // First show confirmation
        showConfirm('Are you sure you want to clear all availability?', 'Confirm Clear').then(
          (confirmed) => {
            if (confirmed) {
              // Return an empty object to clear all availability
              setAvailability({});
            }
          }
        );
        return; // Early return to prevent the setAvailability call below
    }

    setAvailability(newAvailability);
  };

  const upgradeToPreferred = async () => {
    // Show confirmation dialog
    const confirmed = await showConfirm(
      'This will convert all available slots to preferred status. Continue?',
      'Confirm Upgrade'
    );

    if (confirmed) {
      const newAvailability = { ...availability };
      for (let day = 0; day < 7; day++) {
        if (newAvailability[day]) {
          for (const timeSlot in newAvailability[day]) {
            if (newAvailability[day][timeSlot] === AVAILABILITY_TYPES.AVAILABLE) {
              newAvailability[day][timeSlot] = AVAILABILITY_TYPES.PREFERRED;
            }
          }
        }
      }
      setAvailability(newAvailability);
    }
  };

  // Quick add functions similar to mobile version
  const handleQuickAddSlot = (dayIndex, start_time, end_time, availability_type) => {
    const newAvailability = { ...availability };
    if (!newAvailability[dayIndex]) {
      newAvailability[dayIndex] = {};
    }

    // Fill in all time slots between start and end
    const startIndex = TIME_SLOTS.indexOf(start_time);
    const endIndex = TIME_SLOTS.indexOf(end_time);

    if (startIndex === -1 || endIndex === -1) {
      showNotification('Invalid time slots', 'error');
      return;
    }

    for (let i = startIndex; i < endIndex; i++) {
      newAvailability[dayIndex][TIME_SLOTS[i]] = availability_type;
    }

    setAvailability(newAvailability);
  };

  const handleMakeEntireDayAvailable = (dayIndex) => {
    const newAvailability = { ...availability };
    if (!newAvailability[dayIndex]) {
      newAvailability[dayIndex] = {};
    }

    // Make the entire day available (6:00 AM to 10:00 PM)
    const startHour = 6;
    const endHour = 22;

    for (let hour = startHour; hour < endHour; hour++) {
      for (const minute of ['00', '30']) {
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute}`;
        if (TIME_SLOTS.includes(timeSlot)) {
          newAvailability[dayIndex][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
        }
      }
    }

    setAvailability(newAvailability);
  };

  /** Fill every grid half-hour with unavailable so it is persisted and visible in the planner. */
  const handleMarkWholeDayUnavailable = (dayIndex) => {
    const newAvailability = { ...availability };
    if (!newAvailability[dayIndex]) {
      newAvailability[dayIndex] = {};
    }

    const startHour = 6;
    const endHour = 22;

    for (let hour = startHour; hour < endHour; hour++) {
      for (const minute of ['00', '30']) {
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute}`;
        if (TIME_SLOTS.includes(timeSlot)) {
          newAvailability[dayIndex][timeSlot] = AVAILABILITY_TYPES.UNAVAILABLE;
        }
      }
    }

    setAvailability(newAvailability);
  };

  /** Day column header only: mark 07:00–22:00 grid slots unavailable (cells keep their own right-click / right-drag). */
  const handleDayHeaderContextMenu = (e, dayIndex) => {
    e.preventDefault();
    e.stopPropagation();
    handleMarkWholeDayUnavailable(dayIndex);
  };

  // Helper function to add availability before/after an event with buffer
  const addAvailabilityAroundEvent = (event, direction, minutes = 30, dayIndex) => {
    const newAvailability = { ...availability };
    if (!newAvailability[dayIndex]) {
      newAvailability[dayIndex] = {};
    }

    const eventStart = new Date(event.start_time);
    const eventEnd = new Date(event.end_time);
    const dayDate = addDays(selectedWeek, dayIndex);
    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const bufferMinutes = 30; // 30-minute buffer

    if (direction === 'before') {
      // Add buffer: start 30 minutes earlier than the requested time
      const beforeStart = new Date(eventStart.getTime() - (minutes + bufferMinutes) * 60 * 1000);
      const beforeEnd = new Date(eventStart.getTime() - bufferMinutes * 60 * 1000);

      if (beforeStart >= dayStart) {
        const startIndex = TIME_SLOTS.findIndex((time) => {
          const [h, m] = time.split(':').map(Number);
          const slotTime = new Date(dayDate);
          slotTime.setHours(h, m, 0, 0);
          return slotTime >= beforeStart;
        });
        if (startIndex !== -1) {
          const endIndex =
            TIME_SLOTS.findIndex((time) => {
              const [h, m] = time.split(':').map(Number);
              const slotTime = new Date(dayDate);
              slotTime.setHours(h, m, 0, 0);
              return slotTime >= beforeEnd;
            }) - 1;
          if (endIndex >= startIndex) {
            for (let i = startIndex; i <= endIndex; i++) {
              const timeSlot = TIME_SLOTS[i];
              if (
                !newAvailability[dayIndex][timeSlot] ||
                newAvailability[dayIndex][timeSlot] === AVAILABILITY_TYPES.AVAILABLE
              ) {
                newAvailability[dayIndex][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
              }
            }
          }
        }
      }
    } else if (direction === 'after') {
      // Add buffer: start 30 minutes later than the event ends
      const afterStart = new Date(eventEnd.getTime() + bufferMinutes * 60 * 1000);
      const afterEnd = new Date(eventEnd.getTime() + (minutes + bufferMinutes) * 60 * 1000);

      const dayEnd = new Date(dayDate);
      dayEnd.setHours(23, 59, 59, 999);
      if (afterEnd <= dayEnd) {
        const startIndex = TIME_SLOTS.findIndex((time) => {
          const [h, m] = time.split(':').map(Number);
          const slotTime = new Date(dayDate);
          slotTime.setHours(h, m, 0, 0);
          return slotTime >= afterStart;
        });
        if (startIndex !== -1) {
          const endIndex =
            TIME_SLOTS.findIndex((time) => {
              const [h, m] = time.split(':').map(Number);
              const slotTime = new Date(dayDate);
              slotTime.setHours(h, m, 0, 0);
              return slotTime >= afterEnd;
            }) - 1;
          if (endIndex >= startIndex) {
            for (let i = startIndex; i <= endIndex; i++) {
              const timeSlot = TIME_SLOTS[i];
              if (
                !newAvailability[dayIndex][timeSlot] ||
                newAvailability[dayIndex][timeSlot] === AVAILABILITY_TYPES.AVAILABLE
              ) {
                newAvailability[dayIndex][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
              }
            }
          }
        }
      }
    }

    setAvailability(newAvailability);
    triggerSave();
  };

  // Function to merge consecutive calendar events for a specific day with dynamic block processing
  const getMergedCalendarEventsForDay = (dayIndex) => {
    const currentDate = addDays(selectedWeek, dayIndex);
    const currentDateStr = format(currentDate, 'yyyy-MM-dd');

    // Get all events for this day
    const dayEvents = filteredCalendarEvents.filter((event) => {
      const eventStart = new Date(event.start_time);
      const eventDateStr = format(eventStart, 'yyyy-MM-dd');
      return eventDateStr === currentDateStr;
    });

    // Group events by calendar name and merge consecutive ones
    const mergedEvents = [];
    const eventGroups = {};

    // Group events by calendar name
    dayEvents.forEach((event) => {
      const calendarName = event.calendar_name;
      if (!eventGroups[calendarName]) {
        eventGroups[calendarName] = [];
      }
      eventGroups[calendarName].push(event);
    });

    // Merge consecutive events for each calendar
    Object.entries(eventGroups).forEach(([calendarName, events]) => {
      // Sort events by start time
      const sortedEvents = events.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      let currentMergedEvent = null;

      sortedEvents.forEach((event) => {
        const eventStart = new Date(event.start_time);
        const eventEnd = new Date(event.end_time);

        if (!currentMergedEvent) {
          // Start a new merged event
          currentMergedEvent = {
            ...event,
            originalEvents: [event],
          };
        } else {
          const currentEnd = new Date(currentMergedEvent.end_time);

          // Check if this event is consecutive (starts when the previous one ends, or overlaps)
          if (eventStart <= currentEnd) {
            // Merge the events
            currentMergedEvent.end_time =
              eventEnd > currentEnd ? eventEnd.toISOString() : currentMergedEvent.end_time;
            currentMergedEvent.originalEvents.push(event);

            // Update summary if needed (use the longest one or combine)
            if (event.summary.length > currentMergedEvent.summary.length) {
              currentMergedEvent.summary = event.summary;
            }
          } else {
            // Not consecutive, save current merged event and start a new one
            mergedEvents.push(currentMergedEvent);
            currentMergedEvent = {
              ...event,
              originalEvents: [event],
            };
          }
        }
      });

      // Don't forget the last merged event
      if (currentMergedEvent) {
        mergedEvents.push(currentMergedEvent);
      }
    });

    // Process the merged events to create dynamic blocks
    return processEventBlocks(mergedEvents, TIME_SLOTS, currentDate);
  };

  return (
    <div className="space-y-3 px-2" onMouseUp={handleMouseUp}>
      {/* Header with quick presets and legend */}
      <div className="card p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Status chip moved to main page header */}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => (window.location.href = '/settings')}
              className="btn-secondary flex items-center gap-1 px-3 py-2 text-sm"
              title="Manage calendar integrations"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Manage Calendars
            </button>
            <button
              onClick={copyToNextWeek}
              className="btn-secondary flex items-center gap-1 px-3 py-2 text-sm"
              title="Copy current availability to next week"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              Copy to Next Week
            </button>
            <button
              onClick={() => saveAvailability()}
              disabled={isSaving}
              className={`btn-primary px-4 py-2 text-sm ${isSaving ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {isSaving ? 'Saving...' : 'Save Now'}
            </button>
          </div>
        </div>

        {/* Crew-mates Filter Section - More Prominent */}
        <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-purple-900">Crew-mates Availability</span>
              </div>

              {/* Switch Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCrewmates(!showCrewmates)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${showCrewmates ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  role="switch"
                  aria-checked={showCrewmates}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showCrewmates ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
                <span className="text-sm font-medium text-purple-900">
                  {showCrewmates ? 'On' : 'Off'}
                </span>
              </div>
            </div>

            {showCrewmates && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-purple-700">
                  {loadingCrewmates ? 'Loading…' : `${totalCrewmates} people`}
                </span>
              </div>
            )}
          </div>

          {/* Filter Options - Only show when enabled */}
          {showCrewmates && crewOptions?.length > 0 && (
            <div className="mt-3">
              <div className="mb-2 text-sm font-medium text-purple-800">Filter by crew:</div>
              <div className="flex flex-wrap gap-2">
                {crewOptions.map((c) => {
                  const active = crewFilterIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => {
                        if (active) {
                          setCrewFilterIds(crewFilterIds.filter((id) => id !== c.id));
                        } else {
                          setCrewFilterIds([...crewFilterIds, c.id]);
                        }
                      }}
                      className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${active
                        ? 'border-2 border-purple-300 bg-purple-200 text-purple-800'
                        : 'border-2 border-purple-200 bg-white text-purple-700 hover:bg-purple-100'
                        }`}
                      title={c.name}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Availability summary indicator */}
          {showCrewmates && (
            <div className="mt-3">
              <div className="mb-2 text-sm font-medium text-purple-800">
                Week availability pattern:
              </div>
              <div className="grid grid-cols-7 gap-1">
                {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
                  // Calculate average availability for this day
                  let totalSlots = 0;
                  let totalAvailable = 0;

                  for (let hour = 6; hour <= 22; hour++) {
                    for (const minute of ['00', '30']) {
                      const timeSlot = `${hour.toString().padStart(2, '0')}:${minute}`;
                      const key = `${dayIndex}-${timeSlot}`;
                      const counts = crewmateCountsMap[key];
                      if (counts) {
                        totalSlots++;
                        totalAvailable += (counts.available || 0) + (counts.preferred || 0);
                      }
                    }
                  }

                  const avgPercentage =
                    totalSlots > 0 ? (totalAvailable / (totalSlots * totalCrewmates)) * 100 : 0;

                  let bgColor = 'bg-gray-200';
                  if (avgPercentage >= 60) bgColor = 'bg-green-400';
                  else if (avgPercentage >= 40) bgColor = 'bg-yellow-400';
                  else if (avgPercentage >= 20) bgColor = 'bg-orange-400';
                  else if (avgPercentage > 0) bgColor = 'bg-red-400';

                  return (
                    <div key={dayIndex} className="text-center">
                      <div
                        className={`h-6 w-full rounded-sm ${bgColor}`}
                        title={`${DAYS_OF_WEEK[dayIndex]} - ${avgPercentage.toFixed(0)}% avg availability`}
                      ></div>
                      <div className="mt-1 text-[8px] text-gray-600">
                        {DAYS_OF_WEEK[dayIndex].slice(0, 3)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Calendar Integration Section - Always show the card, toggle controls visibility */}
        {calendarIntegrations.length > 0 && (
          <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-900">Calendar Events</span>
                </div>

                {/* Calendar View Toggle - Same position as crew-mates toggle */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowCalendarView && setShowCalendarView(!showCalendarView)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${showCalendarView ? 'bg-blue-600' : 'bg-gray-200'}`}
                    role="switch"
                    aria-checked={showCalendarView}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showCalendarView ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                  <span className="text-sm font-medium text-blue-900">
                    {showCalendarView ? 'On' : 'Off'}
                  </span>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-blue-300 bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                <span>
                  {loadingCalendarEvents
                    ? 'Loading…'
                    : `${filteredCalendarEvents.length} events this week`}
                </span>
              </div>
            </div>

            {/* Calendar content - only show when calendar view is enabled */}
            {showCalendarView && (
              <>
                {/* Calendar Chips - Show all configured calendars, even if no events */}
                <div className="mt-3">
                  <div className="mb-2 text-sm font-medium text-blue-800">Active calendars:</div>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      // Get calendar names from active integrations only
                      const calendarNames = calendarIntegrations
                        .filter((integration) => integration.is_active)
                        .map((integration) => integration.name);

                      // Debug logging
                      console.log('Calendar integrations:', calendarIntegrations);
                      console.log('Active calendar names from integrations:', calendarNames);
                      console.log('Calendar events:', calendarEvents);
                      console.log('Calendar names from events:', [
                        ...new Set(calendarEvents.map((event) => event.calendar_name)),
                      ]);
                      console.log('Enabled calendars:', Array.from(enabledCalendars));

                      const getCalendarColor = (calendarName) => {
                        if (calendarName.toLowerCase().includes('work')) {
                          return 'bg-blue-200 border-blue-300 text-blue-800';
                        } else if (calendarName.toLowerCase().includes('personal')) {
                          return 'bg-green-200 border-green-300 text-green-800';
                        } else if (calendarName.toLowerCase().includes('family')) {
                          return 'bg-purple-200 border-purple-300 text-purple-800';
                        }

                        const colors = [
                          'bg-blue-200 border-blue-300 text-blue-800',
                          'bg-green-200 border-green-300 text-green-800',
                          'bg-purple-200 border-purple-300 text-purple-800',
                          'bg-orange-200 border-orange-300 text-orange-800',
                          'bg-pink-200 border-pink-300 text-pink-800',
                          'bg-indigo-200 border-indigo-300 text-indigo-800',
                        ];

                        let hash = 0;
                        for (let i = 0; i < calendarName.length; i++) {
                          hash = ((hash << 5) - hash + calendarName.charCodeAt(i)) & 0xffffffff;
                        }
                        return colors[Math.abs(hash) % colors.length];
                      };

                      return calendarNames.map((calendarName) => {
                        const eventCount = calendarEvents.filter(
                          (event) => event.calendar_name === calendarName
                        ).length;
                        const isEnabled =
                          enabledCalendars.size === 0 || enabledCalendars.has(calendarName);

                        return (
                          <button
                            key={calendarName}
                            onClick={() => {
                              console.log('Toggling calendar:', calendarName);
                              console.log(
                                'Current enabled calendars:',
                                Array.from(enabledCalendars)
                              );
                              toggleCalendar(calendarName);
                            }}
                            className={`rounded-full border-2 px-3 py-1 text-sm font-medium transition-colors ${isEnabled
                              ? getCalendarColor(calendarName)
                              : 'border-gray-300 bg-gray-100 text-gray-500'
                              } hover:opacity-80`}
                            title={`${calendarName} (${eventCount} events) - Click to toggle visibility`}
                          >
                            <div className="flex items-center gap-1">
                              <span>{calendarName}</span>
                              <span className="text-xs opacity-75">({eventCount})</span>
                            </div>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* No Calendar Integrations Message - Only show when no active integrations exist */}
                {calendarIntegrations.filter((integration) => integration.is_active).length ===
                  0 && (
                    <div className="mt-3 text-center">
                      <p className="text-sm text-blue-700">
                        Add calendar integrations to see your appointments here
                      </p>
                      <button
                        onClick={() => (window.location.href = '/settings')}
                        className="btn-primary mt-2 px-3 py-1 text-sm"
                      >
                        Manage Calendar Integrations
                      </button>
                    </div>
                  )}

                {/* No Events This Week Message - Show when active integrations exist but no events */}
                {calendarIntegrations.filter((integration) => integration.is_active).length > 0 &&
                  calendarEvents.length === 0 && (
                    <div className="mt-3 text-center">
                      <p className="text-sm text-blue-700">
                        No events this week from your calendar integrations
                      </p>
                    </div>
                  )}

                {/* No Events Match Filters Message - Show when events exist but none match filters */}
                {calendarEvents.length > 0 && filteredCalendarEvents.length === 0 && (
                  <div className="mt-3 text-center">
                    <p className="text-sm text-blue-700">
                      No events this week match your current filters
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Weekly Stats */}
      <div className="mb-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-blue-50 p-2">
          <div className="font-semibold text-blue-700">{weeklyStats.daysWithAvailability}/7</div>
          <div className="text-xs text-blue-600">Days Covered</div>
        </div>
        <div
          className={`rounded-lg p-2 ${weeklyStats.allDaysCovered ? 'bg-green-50' : 'bg-yellow-50'}`}
        >
          <div
            className={`font-semibold ${weeklyStats.allDaysCovered ? 'text-green-700' : 'text-yellow-700'}`}
          >
            {weeklyStats.allDaysCovered ? 'Complete' : 'Incomplete'}
          </div>
          <div
            className={`text-xs ${weeklyStats.allDaysCovered ? 'text-green-600' : 'text-yellow-600'}`}
          >
            Week Status
          </div>
        </div>
        <div className="rounded-lg bg-gray-50 p-2">
          <div className="text-sm font-semibold text-gray-700">
            {weeklyStats.availableSlots + weeklyStats.preferredSlots}
          </div>
          <div className="text-xs text-gray-600">Total Slots</div>
        </div>
      </div>

      {/* Legend - Inline with stats */}
      <div className="mb-1 flex flex-wrap items-center justify-center gap-4">
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-sm border border-dashed border-gray-300 bg-gray-50"></div>
          <span className="text-xs text-gray-600">Not set</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-sm border border-red-300 bg-red-200"></div>
          <span className="text-xs text-gray-600">Unavailable</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-sm border border-gray-200 bg-blue-200"></div>
          <span className="text-xs text-gray-600">Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-sm border border-gray-200 bg-green-200"></div>
          <span className="text-xs text-gray-600">Preferred</span>
        </div>
        {showCrewmates && (
          <>
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded-sm border border-gray-200 bg-green-500/60"></div>
              <span className="text-xs text-gray-600">High crew availability (80%+)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded-sm border border-gray-200 bg-yellow-400/50"></div>
              <span className="text-xs text-gray-600">Moderate crew availability (40-60%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded-sm border border-gray-200 bg-red-400/50"></div>
              <span className="text-xs text-gray-600">Low crew availability (&lt;20%)</span>
            </div>
          </>
        )}

        {/* Calendar Events Legend */}
        {calendarEvents.length > 0 && (
          <>
            {(() => {
              // Get unique calendar names
              const uniqueCalendarNames = [
                ...new Set(calendarEvents.map((event) => event.calendar_name)),
              ];

              // Define color mapping for calendars
              const getCalendarColor = (calendarName, index) => {
                const colors = [
                  'bg-blue-200 border-blue-300',
                  'bg-green-200 border-green-300',
                  'bg-purple-200 border-purple-300',
                  'bg-orange-200 border-orange-300',
                  'bg-pink-200 border-pink-300',
                  'bg-indigo-200 border-indigo-300',
                ];

                // Check for common calendar types
                if (calendarName.toLowerCase().includes('work')) {
                  return 'bg-blue-200 border-blue-300';
                } else if (calendarName.toLowerCase().includes('personal')) {
                  return 'bg-green-200 border-green-300';
                } else if (calendarName.toLowerCase().includes('family')) {
                  return 'bg-purple-200 border-purple-300';
                }

                return colors[index % colors.length];
              };

              return uniqueCalendarNames.map((calendarName, index) => (
                <div key={calendarName} className="flex items-center gap-1">
                  <div
                    className={`h-3 w-3 rounded-sm border ${getCalendarColor(calendarName, index)}`}
                  ></div>
                  <span className="text-xs text-gray-600">{calendarName}</span>
                </div>
              ));
            })()}
          </>
        )}
      </div>

      {/* Unified Grid Layout - Toggl Style */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Weekly Availability & Calendar</h3>
          <div className="relative inline-flex">
            <button
              type="button"
              aria-expanded={desktopGridHelpOpen}
              aria-controls="availability-desktop-grid-help"
              onClick={() => setDesktopGridHelpOpen((prev) => !prev)}
              className="inline-flex rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rowing-500 focus-visible:ring-offset-2"
              title="Help: how to edit the grid"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden />
              <span className="sr-only">Help: how to edit availability grid</span>
            </button>
            {desktopGridHelpOpen && (
              <div
                id="availability-desktop-grid-help"
                role="region"
                aria-label="Availability grid editing help"
                className="absolute left-0 top-full z-40 mt-1.5 w-[min(calc(100vw-4rem),24rem)] max-w-md rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm leading-relaxed text-gray-600 shadow-lg"
              >
              <p className="mb-0 whitespace-pre-line">
                {`Dashed = not saved yet
Solid red = unavailable
Left paints available
Right paints unavailable
Right-click weekday header marks whole day unavailable`}
              </p>
              </div>
            )}
          </div>
        </div>

        {/* Main Grid Container */}
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {/* Week Dates Display */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="grid grid-cols-8 gap-0">
              <div className="border-r border-gray-300 bg-gradient-to-r from-blue-50 to-blue-100 p-2 text-xs font-semibold text-gray-800">
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => setCompactTimeDisplay(!compactTimeDisplay)}
                    className="text-xs hover:text-blue-600"
                    title={compactTimeDisplay ? 'Show full time' : 'Show compact time'}
                  >
                    {compactTimeDisplay ? 'Time↕' : 'Time'}
                  </button>
                </div>
              </div>
              {weekDates.map((date, index) => (
                <div
                  key={index}
                  className="cursor-default border-r border-gray-300 bg-gradient-to-b from-white to-gray-50 p-1 text-center transition-colors duration-200 hover:bg-blue-50"
                  title={`${DAYS_OF_WEEK[index]} — right-click: mark whole day unavailable`}
                  aria-label={`${DAYS_OF_WEEK[index]} ${format(date, 'MMM d')}`}
                  onContextMenu={(e) => handleDayHeaderContextMenu(e, index)}
                >
                  <div className="text-xs font-semibold text-gray-900">
                    {DAYS_OF_WEEK_SHORT[index]}
                  </div>
                  <div className="text-xs font-medium text-blue-600">
                    {date.getDate()}/{date.getMonth() + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Add Buttons Row */}
          <div className="border-x border-gray-200 bg-gray-50">
            <div className="grid grid-cols-8 gap-0">
              <div className="border-r border-gray-300 bg-gradient-to-r from-blue-50 to-blue-100 p-2 text-xs font-semibold text-gray-800">
                <div className="flex items-center justify-center">Quick Add</div>
              </div>
              {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
                <div key={dayIndex} className="border-r border-gray-300 bg-white p-1">
                  <div className="flex flex-col space-y-1">
                    {/* All Day button */}
                    <button
                      onClick={() => handleMakeEntireDayAvailable(dayIndex)}
                      className="flex items-center justify-center rounded bg-blue-100 px-1 py-0.5 text-xs text-blue-700 transition-colors duration-150 hover:bg-blue-200"
                      title={`Make entire ${DAYS_OF_WEEK[dayIndex]} available (07:00–22:00)`}
                    >
                      <ClockIcon className="mr-0.5 h-3 w-3" />
                      All
                    </button>
                    <button
                      onClick={() => handleMarkWholeDayUnavailable(dayIndex)}
                      className="mt-0.5 flex items-center justify-center rounded bg-gray-200 px-1 py-0.5 text-xs text-gray-800 transition-colors duration-150 hover:bg-gray-300"
                      title={`Mark entire ${DAYS_OF_WEEK[dayIndex]} as unavailable`}
                      type="button"
                    >
                      <NoSymbolIcon className="mr-0.5 h-3 w-3" />
                      Off
                    </button>

                    {/* Quick time slots - 3 most common */}
                    <div className="grid grid-cols-3 gap-0.5">
                      <button
                        onClick={() =>
                          handleQuickAddSlot(
                            dayIndex,
                            '07:00',
                            '09:00',
                            AVAILABILITY_TYPES.AVAILABLE
                          )
                        }
                        className="rounded bg-green-100 px-0.5 py-0.5 text-xs text-green-700 transition-colors duration-150 hover:bg-green-200"
                        title="Early Morning 07:00-09:00"
                      >
                        07
                      </button>
                      <button
                        onClick={() =>
                          handleQuickAddSlot(
                            dayIndex,
                            '09:00',
                            '11:00',
                            AVAILABILITY_TYPES.AVAILABLE
                          )
                        }
                        className="rounded bg-blue-100 px-0.5 py-0.5 text-xs text-blue-700 transition-colors duration-150 hover:bg-blue-200"
                        title="Morning 09:00-11:00"
                      >
                        09
                      </button>
                      <button
                        onClick={() =>
                          handleQuickAddSlot(
                            dayIndex,
                            '11:00',
                            '13:00',
                            AVAILABILITY_TYPES.AVAILABLE
                          )
                        }
                        className="rounded bg-orange-100 px-0.5 py-0.5 text-xs text-orange-700 transition-colors duration-150 hover:bg-orange-200"
                        title="Pre-lunch 11:00-13:00"
                      >
                        11
                      </button>
                    </div>
                    {/* Additional quick slots */}
                    <div className="grid grid-cols-3 gap-0.5">
                      <button
                        onClick={() =>
                          handleQuickAddSlot(
                            dayIndex,
                            '13:00',
                            '15:00',
                            AVAILABILITY_TYPES.AVAILABLE
                          )
                        }
                        className="rounded bg-yellow-100 px-0.5 py-0.5 text-xs text-yellow-700 transition-colors duration-150 hover:bg-yellow-200"
                        title="Post-lunch 13:00-15:00"
                      >
                        13
                      </button>
                      <button
                        onClick={() =>
                          handleQuickAddSlot(
                            dayIndex,
                            '15:00',
                            '17:00',
                            AVAILABILITY_TYPES.AVAILABLE
                          )
                        }
                        className="rounded bg-purple-100 px-0.5 py-0.5 text-xs text-purple-700 transition-colors duration-150 hover:bg-purple-200"
                        title="Afternoon 15:00-17:00"
                      >
                        15
                      </button>
                      <button
                        onClick={() =>
                          handleQuickAddSlot(
                            dayIndex,
                            '17:00',
                            '20:00',
                            AVAILABILITY_TYPES.AVAILABLE
                          )
                        }
                        className="rounded bg-pink-100 px-0.5 py-0.5 text-xs text-pink-700 transition-colors duration-150 hover:bg-pink-200"
                        title="Evening 17:00-20:00"
                      >
                        17
                      </button>
                    </div>

                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Availability Grid - connecting directly with header by removing margin/padding */}
        <div className="overflow-hidden rounded-b-lg border-x border-b border-t-0">
          {/* Grid Content */}
          <div className="overflow-hidden bg-white shadow-sm">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-gray-600">Loading availability...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Time slots */}
                  {TIME_SLOTS.map((timeSlot) => (
                    <div
                      key={timeSlot}
                      className="grid grid-cols-8 gap-0 transition-colors duration-150 hover:bg-gray-50/50"
                    >
                      <div className="border-b border-r border-gray-100 border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 p-1 text-xs font-medium text-gray-700">
                        <div className="flex items-center justify-center">
                          {compactTimeDisplay ? timeSlot.split(':')[0] : timeSlot}
                        </div>
                      </div>
                      {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
                        const currentDate = addDays(selectedWeek, dayIndex);
                        const currentDateStr = format(currentDate, 'yyyy-MM-dd');

                        // Get merged events for this day
                        const mergedEvents = getMergedCalendarEventsForDay(dayIndex);

                        // Get events that overlap with this specific time slot
                        const [hours, minutes] = timeSlot.split(':');
                        const slotStart = new Date(currentDate);
                        slotStart.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                        const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000);

                        const slotEvents = mergedEvents.filter((event) => {
                          const eventStart = new Date(event.start_time);
                          const eventEnd = new Date(event.end_time);
                          return eventStart < slotEnd && eventEnd > slotStart;
                        });

                        return (
                          <div
                            key={`${dayIndex}-${timeSlot}`}
                            className="border-b border-r border-gray-100 border-gray-200 p-0.5"
                          >
                            {/* Split column: Left for availability, Right for calendar events */}
                            <div className={`flex h-full ${showCalendarView ? '' : 'flex-col'}`}>
                              {/* Left side - Availability toggle - Always visible and functional */}
                              <div
                                className={`${showCalendarView ? 'flex-1 border-r border-gray-200' : 'w-full'} ${!showCalendarView ? 'bg-gray-50' : ''}`}
                              >
                                <div
                                  className={`h-full min-h-[20px] ${getCellClass(dayIndex, timeSlot)}`}
                                  onClick={() => handleCellClick(dayIndex, timeSlot)}
                                  onContextMenu={(e) => handleRightClick(e, dayIndex, timeSlot)}
                                  onMouseDown={(e) => handleMouseDown(e, dayIndex, timeSlot)}
                                  onMouseEnter={() => handleMouseEnter(dayIndex, timeSlot)}
                                  title={`${DAYS_OF_WEEK[dayIndex]} ${timeSlot} — ${getSlotStateLabel(availability[dayIndex], timeSlot)}`}
                                >
                                  {showCrewmates && (
                                    <div className="pointer-events-none absolute inset-0 flex items-start justify-end p-0.5">
                                      {(() => {
                                        const key = `${dayIndex}-${timeSlot}`;
                                        const counts = crewmateCountsMap[key];
                                        if (!counts) return null;
                                        const total =
                                          (counts.available || 0) + (counts.preferred || 0);
                                        if (!total) return null;

                                        const bgColor = getCrewmatesAvailabilityColor(
                                          counts,
                                          totalCrewmates
                                        );
                                        const textColor = getCrewmatesTextColor(
                                          counts,
                                          totalCrewmates
                                        );

                                        return (
                                          <span
                                            className={`rounded px-1 text-[10px] ${bgColor} ${textColor}`}
                                            title={`${counts.preferred} preferred, ${counts.available} available (${Math.round((total / totalCrewmates) * 100)}% of crew)`}
                                          >
                                            {total}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Right side - Calendar events (only shown when calendar view is enabled) */}
                              {showCalendarView && (
                                <div className="relative flex-1">
                                  {/* Add button for consecutive empty cells in the right pane */}
                                  {(() => {
                                    const hasEvents = slotEvents.length > 0;

                                    // Show add button only if there are no events in this slot
                                    if (!hasEvents) {
                                      // Check if this is the start of a consecutive empty block
                                      const currentSlotIndex = TIME_SLOTS.indexOf(timeSlot);
                                      const isStartOfEmptyBlock = (() => {
                                        // Check if previous slot has events
                                        if (currentSlotIndex > 0) {
                                          const prevTimeSlot = TIME_SLOTS[currentSlotIndex - 1];
                                          const prevSlotStart = new Date(currentDate);
                                          const [prevHours, prevMinutes] = prevTimeSlot.split(':');
                                          prevSlotStart.setHours(parseInt(prevHours), parseInt(prevMinutes), 0, 0);
                                          const prevSlotEnd = new Date(prevSlotStart.getTime() + 30 * 60 * 1000);

                                          const prevSlotEvents = mergedEvents.filter((event) => {
                                            const eventStart = new Date(event.start_time);
                                            const eventEnd = new Date(event.end_time);
                                            return eventStart < prevSlotEnd && eventEnd > prevSlotStart;
                                          });

                                          if (prevSlotEvents.length > 0) {
                                            return true; // Previous slot has events, so this is the start
                                          }
                                        } else {
                                          return true; // First slot of the day
                                        }

                                        // Check if previous slot is also empty
                                        if (currentSlotIndex > 0) {
                                          const prevTimeSlot = TIME_SLOTS[currentSlotIndex - 1];
                                          const prevSlotStart = new Date(currentDate);
                                          const [prevHours, prevMinutes] = prevTimeSlot.split(':');
                                          prevSlotStart.setHours(parseInt(prevHours), parseInt(prevMinutes), 0, 0);
                                          const prevSlotEnd = new Date(prevSlotStart.getTime() + 30 * 60 * 1000);

                                          const prevSlotEvents = mergedEvents.filter((event) => {
                                            const eventStart = new Date(event.start_time);
                                            const eventEnd = new Date(event.end_time);
                                            return eventStart < prevSlotEnd && eventEnd > prevSlotStart;
                                          });

                                          if (prevSlotEvents.length === 0) {
                                            return false; // Previous slot is also empty, so this is not the start
                                          }
                                        }

                                        return true; // Default to start if we can't determine
                                      })();

                                      // Calculate the end time of the consecutive empty block
                                      const getEndTimeOfEmptyBlock = (() => {
                                        let endTime = null;
                                        for (let i = currentSlotIndex + 1; i < TIME_SLOTS.length; i++) {
                                          const nextTimeSlot = TIME_SLOTS[i];
                                          const nextSlotStart = new Date(currentDate);
                                          const [nextHours, nextMinutes] = nextTimeSlot.split(':');
                                          nextSlotStart.setHours(parseInt(nextHours), parseInt(nextMinutes), 0, 0);
                                          const nextSlotEnd = new Date(nextSlotStart.getTime() + 30 * 60 * 1000);

                                          const nextSlotEvents = mergedEvents.filter((event) => {
                                            const eventStart = new Date(event.start_time);
                                            const eventEnd = new Date(event.end_time);
                                            return eventStart < nextSlotEnd && eventEnd > nextSlotStart;
                                          });

                                          if (nextSlotEvents.length > 0) {
                                            // Found an event, so the empty block ends at the start of this slot
                                            endTime = nextSlotStart;
                                            break;
                                          }
                                        }

                                        // If no events found, the empty block extends to 22:00 (capped)
                                        if (!endTime) {
                                          endTime = new Date(currentDate);
                                          endTime.setHours(22, 0, 0, 0);
                                        }

                                        return endTime;
                                      })();

                                      // Only render the button at the start of consecutive empty blocks
                                      if (isStartOfEmptyBlock) {
                                        const slotHeight = 24; // Same as used for events

                                        // Calculate the actual duration in milliseconds
                                        const blockStartTime = new Date(currentDate);
                                        const [startHours, startMinutes] = timeSlot.split(':');
                                        blockStartTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

                                        // Cap the start time at 07:00
                                        const dayStartTime = new Date(currentDate);
                                        dayStartTime.setHours(7, 0, 0, 0);
                                        const effectiveStartTime = blockStartTime < dayStartTime ? dayStartTime : blockStartTime;

                                        const durationMs = getEndTimeOfEmptyBlock - effectiveStartTime;
                                        const heightPx = Math.max(2, (durationMs / (30 * 60 * 1000)) * slotHeight);

                                        const endTimeSlot = getEndTimeOfEmptyBlock.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                        return (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation(); // Prevent triggering the cell click
                                              // Add availability for all time slots in the empty block
                                              const blockStartTime = new Date(currentDate);
                                              const [startHours, startMinutes] = timeSlot.split(':');
                                              blockStartTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

                                              // Cap the start time at 07:00
                                              const dayStartTime = new Date(currentDate);
                                              dayStartTime.setHours(7, 0, 0, 0);
                                              const effectiveStartTime = blockStartTime < dayStartTime ? dayStartTime : blockStartTime;

                                              // Find all time slots that fall within the empty block
                                              for (let i = 0; i < TIME_SLOTS.length; i++) {
                                                const slotTime = TIME_SLOTS[i];
                                                const [slotHours, slotMinutes] = slotTime.split(':');
                                                const slotDateTime = new Date(currentDate);
                                                slotDateTime.setHours(parseInt(slotHours), parseInt(slotMinutes), 0, 0);

                                                // Check if this slot is within the empty block (capped at 07:00-22:00)
                                                if (slotDateTime >= effectiveStartTime && slotDateTime < getEndTimeOfEmptyBlock) {
                                                  handleAddAvailability(dayIndex, slotTime);
                                                }
                                              }
                                            }}
                                            className="absolute left-1 right-1 flex items-center justify-center bg-blue-50 opacity-0 hover:opacity-100 transition-opacity duration-150 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 border border-blue-200 hover:border-blue-300 rounded-sm"
                                            style={{
                                              height: `${heightPx}px`,
                                              top: '0px',
                                              zIndex: 10
                                            }}
                                            title={`Add availability for ${DAYS_OF_WEEK[dayIndex]} from ${timeSlot} to ${endTimeSlot}`}
                                          >
                                            <div className="flex items-center justify-center text-blue-600 hover:text-blue-700 font-medium text-sm">
                                              <PlusIcon className="h-5 w-5 mr-1" />
                                              Add Availability
                                            </div>
                                          </button>
                                        );
                                      }
                                    }
                                    return null;
                                  })()}

                                  {slotEvents.map((event, eventIndex) => {
                                    // Use the processed event block data
                                    const { startIndex, endIndex, originalStart, originalEnd } = event;

                                    // Only render in the slot that contains the event start
                                    const currentSlotIndex = TIME_SLOTS.indexOf(timeSlot);
                                    if (currentSlotIndex !== startIndex) {
                                      return null; // Don't render in subsequent slots
                                    }

                                    // Calculate dimensions using the new utility
                                    const dimensions = calculateEventBlockDimensions(
                                      event,
                                      TIME_SLOTS,
                                      currentDate,
                                      25 // slot height - adjusted to match actual cell height
                                    );

                                    // Get event color
                                    const getEventColor = (calendarName) => {
                                      if (calendarName.toLowerCase().includes('work')) {
                                        return 'bg-blue-200 border-blue-300 text-blue-800';
                                      } else if (calendarName.toLowerCase().includes('personal')) {
                                        return 'bg-green-200 border-green-300 text-green-800';
                                      } else if (calendarName.toLowerCase().includes('family')) {
                                        return 'bg-purple-200 border-purple-300 text-purple-800';
                                      }

                                      const colors = [
                                        'bg-blue-200 border-blue-300 text-blue-800',
                                        'bg-green-200 border-green-300 text-green-800',
                                        'bg-purple-200 border-purple-300 text-purple-800',
                                        'bg-orange-200 border-orange-300 text-orange-800',
                                        'bg-pink-200 border-pink-300 text-pink-800',
                                        'bg-indigo-200 border-indigo-300 text-indigo-800',
                                      ];

                                      let hash = 0;
                                      for (let i = 0; i < calendarName.length; i++) {
                                        hash =
                                          ((hash << 5) - hash + calendarName.charCodeAt(i)) &
                                          0xffffffff;
                                      }
                                      return colors[Math.abs(hash) % colors.length];
                                    };

                                    return (
                                      <div
                                        key={eventIndex}
                                        className={`absolute left-1 right-1 rounded-sm border text-xs font-medium ${getEventColor(event.calendar_name)}`}
                                        style={{
                                          height: `${dimensions.heightPx}px`,
                                          top: `${dimensions.topOffsetPx}px`,
                                          zIndex: 10,
                                        }}
                                        title={`${event.summary} (${originalStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${originalEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
                                      >
                                        <div className="flex h-full items-start justify-start px-1 pt-0.5">
                                          <span className="hyphens-auto break-words text-xs leading-tight">
                                            {event.summary}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopAvailabilityGrid;
